import React, { Component } from 'react';
import { Col, ButtonGroup, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import path from 'path';
import Dropzone from 'react-dropzone';
import Queue from 'promise-queue';
import ScaleLoader from 'react-spinners/ScaleLoader';
import { withFormContext, FormInputLabel } from 'blaze-form';

import toastr from '../../toastr';
import config from '../../../config';
import Api from '../../../services/ApiService';
import FileService from '../../../services/FileService';
import FileList from '../misc/FileList';
import { getFileTypeImage } from '../../../util/fileTypeImages';
import { bytesToSize } from '../../../util/form';
import '../../../styles/form/upload.scss';

class FormInputUpload extends Component {
  static getDerivedStateFromProps(newProps, prevState) {
    // Sometimes local state needs to be updated based on what happens
    // in Form & FormManager
    if (newProps.touched !== prevState.touched) {
      return {
        touched: newProps.touched,
      };
    }

    // This was mainly to handle changes of validity on submit
    if (newProps.valid !== prevState.valid) {
      return {
        valid: newProps.valid,
      };
    }

    return null;
  }

  constructor(props) {
    super(props);

    this.onDrop = this.onDrop.bind(this);
    this.removeFile = this.removeFile.bind(this);
    this.downloadFile = this.downloadFile.bind(this);
    this.clear = this.clear.bind(this);

    const valid = props.valid;
    const touched = props.touched;
    const value = props.value;

    // set here so it only shows if input has initial value
    this.canDownload = value && this.isSingleFile();

    const files = [];

    if (value) {
      if (Array.isArray(value)) {
        value.forEach(file => {
          files.push({ remote: true, ...file });
        });
      } else {
        files.push({ remote: true, ...value });
      }
    }

    this.state = {
      files,
      touched,
      valid,
      value,
    };
  }

  componentDidMount() {
    const { controlId, context } = this.props;
    context.ee.on(controlId, this.handleEvent.bind(this));
  }

  componentWillUnmount() {
    const { controlId, context } = this.props;
    context.ee.off(controlId);
  }

  onDrop(uploadList) {
    const { field, context } = this.props;
    const files = this.isSingleFile() ? [] : this.state.files;
    const indexes = {};
    const uploadQueue = new Queue(2, 10000);
    const jobs = [];

    context.toggleControls(false);

    const { maxListSize } = field;
    uploadList.forEach((file, index) => {
      if (maxListSize && index + 1 > maxListSize) {
        console.warn(`The maximum number of files for this component is ${maxListSize}`);
        return;
      }

      const formData = new FormData();
      const current = files.push({
        id: file.name,
        filename: file.name,
        originalname: file.name,
        size: file.size,
        uploading: true,
        blob: file.preview,
      });

      // Hard file name limit (QM reasons)
      const fileNameLimit = file.maxLen || 63;

      if (file.name.length > fileNameLimit) {
        toastr.error('Upload failed', `Maximum filename length exeeded. (${fileNameLimit}): ${file.name}`);
        return;
      }

      indexes[`${file.name}${file.size}`] = current - 1;

      this.setState({ files });
      formData.set('files', file, file.name);
      formData.set('target', this.props.field.target || '');

      jobs.push(
        uploadQueue.add(() => {
          return Api.put('upload', formData).then(response => {
            const fileData = response.data.files[0];
            const { originalname, size } = fileData;
            const { blob } = files[indexes[`${originalname}${size}`]];

            files[indexes[`${originalname}${size}`]] = {
              ...fileData,
              uploading: false,
              finalPath: field.finalPath,
              blob,
              useOriginalName: !!field.useOriginalName,
            };

            this.setState({ files });
            this.props.onChange(field.name, files);
            this.props.onBlur(field.name);
            context.toggleControls(true);
          });
        })
      );
    });

    return Promise.all(jobs);
  }

  isSingleFile() {
    const { field } = this.props;

    if (field.maxListSize === 1) {
      return true;
    }

    return false;
  }

  handleEvent({ action }) {
    if (action === 'clear') {
      this.clear();
    }
  }

  clear(event) {
    if (event && event.preventDefault) {
      event.preventDefault();
      event.stopPropagation();
    }
    const { field } = this.props;

    this.setState({ files: [] });
    this.props.onChange(field.name, []);
  }

  hasPreview() {
    if (this.isSingleFile() && this.state.files.length) {
      const isImage = /\.(gif|jpg|png)$/;
      const filename = this.state.files[0].originalname || this.state.files[0].filename;
      if (isImage.test(filename)) {
        return true;
      }
    }

    return false;
  }

  downloadFile(file) {
    const { field } = this.props;

    const source = file.remote ? FileService.downloadPath(file.filename, field.finalPath) : file.blob;
    FileService.downloadDataUri(source, field.useOriginalName && file.originalname ? file.originalname : file.filename);
  }

  removeFile(file = {}) {
    const { field } = this.props;
    const filename = file.filename;

    const files = this.state.files.filter(file => file.filename !== filename);
    this.setState({ files });
    this.props.onChange(field.name, files);
  }

  renderPreview() {
    if (!this.state.files.length) {
      return null;
    }

    const file = this.state.files[0];

    if (file.uploading) {
      return (
        <div className="loader">
          <ScaleLoader color="#CCC" />
        </div>
      );
    }

    const isImage = /\.(gif|jpg|png)$/;
    const filename = file.originalname || file.filename;
    let preview = file.blob;

    if (!isImage.test(filename)) {
      const fileIcon = getFileTypeImage(path.extname(filename).substring(1));
      preview = config.publicUrl(`img/${fileIcon}`);
    }

    const source = preview || FileService.downloadPath(this.state.value.filename, this.props.field.finalPath);

    return (
      <div className="preview" title="Preview">
        <img className="bg" src={source} alt="preview" />
        <div className="fg-wrapper">
          <img className="fg" src={source} width="75px" alt="thumbnail" />
        </div>
      </div>
    );
  }

  renderToolbar() {
    if (!this.isSingleFile()) {
      return null;
    }

    if (this.state.files.length) {
      return (
        <div className="toolbar">
          <ButtonGroup>
            <Button
              variant="secondary"
              title="Download"
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                this.downloadFile(this.state.files[0]);
              }}
            >
              <i className="zmdi zmdi-download zmdi-hc-lg" />
            </Button>
            <Button variant="danger" title="Remove" onClick={this.clear}>
              <i className="zmdi zmdi-close zmdi-hc-lg" />
            </Button>
          </ButtonGroup>
        </div>
      );
    }

    return (
      <div className="toolbar">
        <ButtonGroup>
          <Button
            variant="primary"
            title="Upload"
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              this.dropzone.open();
            }}
          >
            <i className="zmdi zmdi-upload zmdi-hc-lg" />
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  renderContents() {
    // A file has been uploaded and a file path/name has been given
    // if (this.state.value) {
    //   const filename = this.state.value.filename;
    //   return (
    //     <div className="single-file">
    //       <div className="filename">{filename}</div>
    //     </div>
    //   );
    // }

    if (this.isSingleFile() && this.state.files.length) {
      const file = this.state.files[0];
      return (
        <div className="single-file">
          {!file.remote && !file.uploading ? (
            <div className="filename">{`${file.originalname} (${bytesToSize(file.size)})`}</div>
          ) : (
            <div className="filename">{file.filename}</div>
          )}
        </div>
      );
    }

    return (
      <div className="no-file">
        <i className="zmdi zmdi-cloud-upload zmdi-hc-2x" />
        <div className="instruction-wrapper">
          <div className="instruction">DRAG OR CLICK</div>
        </div>
      </div>
    );
  }

  renderDownloadLink() {
    if (!this.canDownload) {
      return null;
    }

    return (
      <div>
        <a onClick={this.downloadFile}>Download</a>
      </div>
    );
  }

  render() {
    const { field, noLabel, style } = this.props;

    const invalid = !this.state.valid && this.state.touched;

    const className = classNames({
      'form-upload': true,
      [style]: !!style,
      'is-invalid': invalid,
      'is-single': this.isSingleFile(),
      'is-empty': this.state.files.length === 0,
      'has-files': this.state.files.length > 0,
      'has-filelist': !this.isSingleFile(),
    });

    const dropzone = (
      <div className="drop-zone-wrapper">
        <Dropzone
          onDrop={this.onDrop}
          className="drop-zone"
          ref={c => {
            this.dropzone = c;
          }}
        >
          <FormInputLabel hide={noLabel}>{field.label}</FormInputLabel>
          {this.renderContents()}
          {this.renderToolbar()}
        </Dropzone>
      </div>
    );

    if (this.isSingleFile()) {
      return (
        <div className={className}>
          <Col className="upload-section">{dropzone}</Col>
          {this.renderPreview()}
        </div>
      );
    }

    return (
      <div className={className}>
        <Col lg={4} className="upload-section">
          {dropzone}
        </Col>
        <Col lg={8} className="upload-section">
          <FileList files={this.state.files} removeFile={this.removeFile} emptyMessage="No Files Uploaded" />
          <div className="clearfix" />
        </Col>
      </div>
    );
  }
}

FormInputUpload.defaultProps = {
  valid: true,
  style: 'plain',
};

FormInputUpload.propTypes = {
  context: PropTypes.object,
  controlId: PropTypes.string,
  field: PropTypes.object,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  noLabel: PropTypes.bool,
  style: PropTypes.string,
  touched: PropTypes.bool,
  valid: PropTypes.bool,

  // Single file, multiple files, nothing (empty string)
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),
};

export default withFormContext(FormInputUpload);
